import { HOME_V2_COLORS } from './colors';

interface HomeV2CloudMascotProps {
  /** Width and height of the mascot (square). Default: 160 */
  size?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Cloud Mascot Component
 * 
 * A friendly cloud character with facial features for the Home V2 content area.
 * Source: docs/references/mockups/home-v2/cloud-mascot.svg
 * 
 * @example
 * ```tsx
 * <HomeV2CloudMascot size={160} />
 * ```
 */
export default function HomeV2CloudMascot({ 
  size = 160,
  className 
}: HomeV2CloudMascotProps) {
  return (
    <svg 
      width={size} 
     // height={size} 
      viewBox="0 30 160 100" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Cloud mascot character"
      role="img"
    >
      {/* Cloud Body - Bottom bar */}
      <path 
        d="M131.717 103.243H28.2831C21.9581 103.243 16.8308 108.37 16.8308 114.695V115.36C16.8308 121.685 21.9581 126.812 28.2831 126.812H131.717C138.042 126.812 143.169 121.685 143.169 115.36V114.695C143.169 108.37 138.042 103.243 131.717 103.243Z" 
        fill={HOME_V2_COLORS.cloudShadow}
      />
      
      {/* Cloud Body - Left puff with shadow */}
      <path 
        d="M29.7231 126.812C42.74 126.812 53.2923 116.26 53.2923 103.243C53.2923 90.2261 42.74 79.6738 29.7231 79.6738C16.7061 79.6738 6.15384 90.2261 6.15384 103.243C6.15384 116.26 16.7061 126.812 29.7231 126.812Z" 
        fill={HOME_V2_COLORS.cloudShadow}
      />
      
      {/* Cloud Body - Right puff */}
      <path 
        d="M134.991 126.812C145.404 126.812 153.846 118.37 153.846 107.957C153.846 97.5433 145.404 89.1014 134.991 89.1014C124.577 89.1014 116.135 97.5433 116.135 107.957C116.135 118.37 124.577 126.812 134.991 126.812Z" 
        fill={HOME_V2_COLORS.cloudBody}
      />
      
      {/* Cloud Body - Upper left puff */}
      <path 
        d="M40.5231 91.3168C50.9366 91.3168 59.3784 82.875 59.3784 72.4615C59.3784 62.0479 50.9366 53.6061 40.5231 53.6061C30.1095 53.6061 21.6677 62.0479 21.6677 72.4615C21.6677 82.875 30.1095 91.3168 40.5231 91.3168Z" 
        fill={HOME_V2_COLORS.cloudBody}
      />
      
      {/* Cloud Body - Upper left shadow */}
      <path 
        d="M22.6954 66.3262C22.0308 68.2523 21.6677 70.3139 21.6677 72.4616C21.6677 82.8739 30.1108 91.3169 40.5231 91.3169C44.4308 91.3169 48.0677 90.1293 51.0769 88.0862C38.7384 85.3416 28.4184 77.2308 22.6954 66.3262Z" 
        fill={HOME_V2_COLORS.cloudShadow}
      />
      
      {/* Cloud Body - Main center */}
      <path 
        d="M87.6554 126.806C113.686 126.806 134.788 105.704 134.788 79.6738C134.788 53.6434 113.686 32.5415 87.6554 32.5415C61.6249 32.5415 40.5231 53.6434 40.5231 79.6738C40.5231 105.704 61.6249 126.806 87.6554 126.806Z" 
        fill={HOME_V2_COLORS.cloudBody}
      />
      
      {/* Cloud Body - Right puff shadow */}
      <path 
        d="M133.637 121.569C127.994 121.569 122.757 119.858 118.406 116.923C121.594 122.812 127.828 126.812 134.997 126.812C143.483 126.812 150.658 121.2 153.022 113.489C148.074 118.48 141.218 121.569 133.637 121.569Z" 
        fill={HOME_V2_COLORS.cloudShadow}
      />
      
      {/* Small decorative puffs */}
      <path 
        d="M14.4431 80.3077C16.3701 80.3077 17.9323 78.7455 17.9323 76.8185C17.9323 74.8914 16.3701 73.3292 14.4431 73.3292C12.516 73.3292 10.9538 74.8914 10.9538 76.8185C10.9538 78.7455 12.516 80.3077 14.4431 80.3077Z" 
        fill={HOME_V2_COLORS.cloudBody}
      />
      <path 
        d="M148.542 82.2584C150.469 82.2584 152.031 80.6962 152.031 78.7691C152.031 76.8421 150.469 75.2799 148.542 75.2799C146.614 75.2799 145.052 76.8421 145.052 78.7691C145.052 80.6962 146.614 82.2584 148.542 82.2584Z" 
        fill={HOME_V2_COLORS.cloudBody}
      />
      <path 
        d="M140.966 84.8431C141.928 84.8431 142.708 84.0633 142.708 83.1015C142.708 82.1397 141.928 81.36 140.966 81.36C140.004 81.36 139.225 82.1397 139.225 83.1015C139.225 84.0633 140.004 84.8431 140.966 84.8431Z" 
        fill={HOME_V2_COLORS.cloudBody}
      />
      
      {/* Center body shadow */}
      <path 
        d="M94.6215 121.378C71.3169 121.378 51.1446 108 41.3538 88.5046C45.4892 110.314 64.6461 126.806 87.6554 126.806C98.1846 126.806 107.914 123.354 115.76 117.514C109.188 120.006 102.068 121.378 94.6215 121.378Z" 
        fill={HOME_V2_COLORS.cloudShadow}
      />
      
      {/* Left eye */}
      <path 
        d="M68.5046 85.9261C68.5046 89.5446 65.5754 91.3538 61.9569 91.3538C58.3385 91.3538 55.4092 89.5384 55.4092 85.9261C55.4092 82.3138 58.3385 79.3784 61.9569 79.3784C65.5754 79.3784 68.5046 82.3076 68.5046 85.9261Z" 
        fill={HOME_V2_COLORS.cloudFace}
      />
      
      {/* Left cheek */}
      <path 
        d="M53.8462 109.754C58.8864 109.754 62.9723 105.668 62.9723 100.628C62.9723 95.5874 58.8864 91.5015 53.8462 91.5015C48.8059 91.5015 44.72 95.5874 44.72 100.628C44.72 105.668 48.8059 109.754 53.8462 109.754Z" 
        fill={HOME_V2_COLORS.cloudCheeks}
        fillOpacity="0.25"
      />
      
      {/* Right eye */}
      <path 
        d="M91.0462 85.9261C91.0462 89.5446 93.9754 91.3538 97.5938 91.3538C101.212 91.3538 104.142 89.5384 104.142 85.9261C104.142 82.3076 101.212 79.3784 97.5938 79.3784C93.9754 79.3784 91.0462 82.3076 91.0462 85.9261Z" 
        fill={HOME_V2_COLORS.cloudFace}
      />
      
      {/* Right cheek */}
      <path 
        d="M105.705 109.754C110.745 109.754 114.831 105.668 114.831 100.628C114.831 95.5874 110.745 91.5015 105.705 91.5015C100.664 91.5015 96.5785 95.5874 96.5785 100.628C96.5785 105.668 100.664 109.754 105.705 109.754Z" 
        fill={HOME_V2_COLORS.cloudCheeks}
        fillOpacity="0.25"
      />
      
      {/* Left eyebrow - line 1 */}
      <path 
        d="M48.4801 82.9415L51.4708 84.5169" 
        stroke={HOME_V2_COLORS.cloudFace} 
        strokeWidth="4" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      
      {/* Smile */}
      <path 
        d="M72.4431 97.723C73.2 101.077 76.1908 103.581 79.7723 103.581C83.3539 103.581 86.3447 101.077 87.1016 97.723" 
        stroke={HOME_V2_COLORS.cloudFace} 
        strokeWidth="4" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      
      {/* Left eyebrow - line 2 */}
      <path 
        d="M48.4801 87.4215L51.4708 84.5168" 
        stroke={HOME_V2_COLORS.cloudFace} 
        strokeWidth="4" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      
      {/* Left eyebrow curve */}
      <path 
        d="M54.3938 72.6031C56.72 71.6184 59.28 71.0769 61.9631 71.0769" 
        stroke={HOME_V2_COLORS.cloudFace} 
        strokeWidth="4" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      
      {/* Left freckle */}
      <path 
        d="M48.0124 95.3415C47.5016 95.0892 46.9847 94.8369 46.4616 94.5846" 
        stroke={HOME_V2_COLORS.cloudCheeks} 
        strokeWidth="4" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      
      {/* Left dimple */}
      <path 
        d="M73.5939 95.5815L70.4123 96.4" 
        stroke={HOME_V2_COLORS.cloudFace} 
        strokeWidth="4" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      
      {/* Right dimple */}
      <path 
        d="M85.9569 95.5815L89.1385 96.4" 
        stroke={HOME_V2_COLORS.cloudFace} 
        strokeWidth="4" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      
      {/* Right eyebrow - line 1 */}
      <path 
        d="M111.071 82.9415L108.08 84.5169" 
        stroke={HOME_V2_COLORS.cloudFace} 
        strokeWidth="4" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      
      {/* Right eyebrow - line 2 */}
      <path 
        d="M111.071 87.4215L108.08 84.5168" 
        stroke={HOME_V2_COLORS.cloudFace} 
        strokeWidth="4" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      
      {/* Right eyebrow curve */}
      <path 
        d="M105.157 72.6031C102.831 71.6184 100.271 71.0769 97.5876 71.0769" 
        stroke={HOME_V2_COLORS.cloudFace} 
        strokeWidth="4" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      
      {/* Right freckle */}
      <path 
        d="M111.539 95.3415C112.049 95.0892 112.566 94.8369 113.089 94.5846" 
        stroke={HOME_V2_COLORS.cloudCheeks} 
        strokeWidth="4" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
    </svg>
  );
}
